/**
 *  @swagger
 *  definitions:
 *    RegattaAdminRequestParams:
 *      type: object
 *      properties:
 *        RegattaId:
 *          description: ID of the associated Regatta
 *          type: string
 *        Name:
 *          description: Name of the RegattaAdminRequest
 *          type: string
 *        Email:
 *          description: Email of the RegattaAdminRequest
 *          type: string
 *        Status:
 *          description: Status of the RegattaAdminRequest
 *          $ref: '#/components/enums/RegattaAdminRequestStatus'
 *
 *  @swagger
 *  definitions:
 *    RegattaAdminRequestCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaAdminRequestParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Email
 *            - Status
 *
 *  @swagger
 *  definitions:
 *    RegattaAdminRequestQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaAdminRequest
 *              type: string
 *        - $ref: '#/definitions/RegattaAdminRequestParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaAdminRequestUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaAdminRequestQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaAdminRequest:
 *      allOf:
 *        - $ref: '#/definitions/RegattaAdminRequestCreateParams'
 *        - $ref: '#/definitions/RegattaAdminRequestUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaAdminRequest:
 *        allOf:
 *          - $ref: '#/definitions/RegattaAdminRequest'
 *          - type: object
 *            properties:
 *              Regatta:
 *                description: Regatta of the RegattaAdminRequest
 *                $ref: '#/components/schemas/Regatta'
 *              Admin:
 *                description: Admin of the RegattaAdminRequest
 *                $ref: '#/components/schemas/Admin'
 */

// Enums
import {
  RegattaAdminRequestStatus,
} from '../enums';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

export type $RelationIds = {
  RegattaId: string;
};

export type $Params = {
  Email: string;
  Name: null | string;
  Status: RegattaAdminRequestStatus;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  Admin?: $Model<'Admin'>;
  Regatta?: $Model<'Regatta'>;
};

export type $ModelParams = $Associations & $CreateParams & {
  id: string;
};

type $Fields = $Params & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  Email: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      isEmail: true,
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: true,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Status: {
    allowNull: false,
    type: Sequelize.ENUM(...Object.values(RegattaAdminRequestStatus)),
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaAdminRequest'> => {
  const RegattaAdminRequest = stack.define(
    'RegattaAdminRequest',
    fields(Sequelize),
    {
      tableName: 'RegattaAdminRequests',
    },
  );

  RegattaAdminRequest.associate = (models: $ModelClasses) => {
    models.RegattaAdminRequest.belongsTo(
      models.Regatta,
      {
        as: 'Regatta',
        foreignKey: 'RegattaId',
      },
    );
    models.RegattaAdminRequest.belongsTo(
      models.Admin,
      {
        foreignKey: 'Email',
        targetKey: 'Email',
      },
    );
  };

  RegattaAdminRequest.addScope(
    'defaultScope',
    {
      order: [['createdAt', 'ASC']],
    },
  );

  return RegattaAdminRequest;
});
